import React, { useState, useEffect, useCallback } from 'react';
import { getUserActivityTimeline } from '../../../../services/userProfileService';
import { formatCallDate } from '../../../../services/callService';
import { formatMemoryDate, getCategoryInfo } from '../../../../services/memoryService';

const ActivityTimeline = ({ activities: initialActivities, userId, onRefresh }) => {
  const [activities, setActivities] = useState(initialActivities || []);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Filter and search activities
  useEffect(() => {
    let filtered = [...activities];

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(activity => activity.activity_type === typeFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(activity => 
        activity.title?.toLowerCase().includes(term) ||
        activity.description?.toLowerCase().includes(term)
      );
    }

    setFilteredActivities(filtered);
  }, [activities, typeFilter, searchTerm]);

  // Load more activities
  const loadMoreActivities = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await getUserActivityTimeline(
        userId, 
        activities.length + 20
      );

      if (fetchError) {
        throw new Error(fetchError);
      }

      if (data && data.length > activities.length) {
        setActivities(data);
        setHasMore(data.length === activities.length + 20);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more activities:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, activities.length, loading, hasMore]);

  // Toggle expanded state
  const toggleExpanded = (activityIndex) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(activityIndex)) {
      newExpanded.delete(activityIndex);
    } else {
      newExpanded.add(activityIndex);
    }
    setExpandedItems(newExpanded);
  };

  // Get activity icon and color
  const getActivityDisplay = (activity) => {
    if (activity.activity_type === 'call') {
      const success = activity.metadata?.success;
      return {
        icon: '📞',
        color: success === true ? 'text-green-600' : success === false ? 'text-red-600' : 'text-blue-600',
        bgColor: success === true ? 'bg-green-100' : success === false ? 'bg-red-100' : 'bg-blue-100'
      };
    } else if (activity.activity_type === 'memory') {
      const categoryInfo = getCategoryInfo(activity.metadata?.category);
      return {
        icon: categoryInfo.icon,
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-100'
      };
    }
    
    return {
      icon: '📝',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100'
    };
  };

  // Format activity metadata for display
  const formatActivityDetails = (activity) => {
    if (activity.activity_type === 'call') {
      const metadata = activity.metadata || {};
      return (
        <div className="space-y-2 text-sm text-gray-600">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="font-medium">Duration:</span> {
                metadata.duration_minutes 
                  ? `${Math.round(metadata.duration_minutes)} minutes`
                  : 'Unknown'
              }
            </div>
            <div>
              <span className="font-medium">Cost:</span> {
                metadata.cost 
                  ? `$${parseFloat(metadata.cost).toFixed(4)}`
                  : 'N/A'
              }
            </div>
            <div>
              <span className="font-medium">Assistant:</span> {
                metadata.assistant_name || 'Unknown'
              }
            </div>
            <div>
              <span className="font-medium">Status:</span> {
                metadata.success === true ? '✅ Successful' : 
                metadata.success === false ? '❌ Failed' : '❓ Unknown'
              }
            </div>
          </div>
          {metadata.ended_reason && (
            <div>
              <span className="font-medium">Ended:</span> {metadata.ended_reason}
            </div>
          )}
        </div>
      );
    } else if (activity.activity_type === 'memory') {
      const metadata = activity.metadata || {};
      const categoryInfo = getCategoryInfo(metadata.category);
      
      return (
        <div className="space-y-2 text-sm text-gray-600">
          <div>
            <span className="font-medium">Category:</span> 
            <span className={`ml-2 inline-flex items-center px-2 py-1 rounded text-xs ${categoryInfo.color}`}>
              {categoryInfo.icon} {categoryInfo.label}
            </span>
          </div>
          {metadata.summary && (
            <div>
              <span className="font-medium">Summary:</span> {metadata.summary}
            </div>
          )}
          {metadata.entities && Object.keys(metadata.entities).length > 0 && (
            <div>
              <span className="font-medium">Entities:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {Object.entries(metadata.entities).map(([key, value]) => (
                  <span key={key} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                    {key}: {Array.isArray(value) ? value.join(', ') : value}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  // Activity type counts for filter badges
  const activityCounts = {
    all: filteredActivities.length,
    call: activities.filter(a => a.activity_type === 'call').length,
    memory: activities.filter(a => a.activity_type === 'memory').length
  };

  if (activities.length === 0 && !loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="text-gray-400 text-4xl mb-4">📝</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Yet</h3>
          <p className="text-gray-600">This user hasn't made any contacts or created memories yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Activity Timeline</h3>
          <button
            onClick={onRefresh}
            className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="Search activities..."
              />
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex space-x-2">
            {[
              { key: 'all', label: 'All', icon: '📋' },
              { key: 'call', label: 'Contacts', icon: '📞' },
              { key: 'memory', label: 'Memories', icon: '🧠' }
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setTypeFilter(filter.key)}
                className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  typeFilter === filter.key
                    ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                }`}
              >
                <span className="mr-1">{filter.icon}</span>
                {filter.label}
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-current bg-white bg-opacity-20 rounded-full">
                  {activityCounts[filter.key]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-md text-sm">
            Error loading timeline: {error}
          </div>
        )}

        <div className="flow-root">
          <ul className="-mb-8">
            {filteredActivities.map((activity, activityIndex) => {
              const display = getActivityDisplay(activity);
              const isExpanded = expandedItems.has(activityIndex);
              const isLast = activityIndex === filteredActivities.length - 1;

              return (
                <li key={`${activity.activity_type}-${activity.activity_date}-${activityIndex}`}>
                  <div className="relative pb-8">
                    {!isLast && (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    )}
                    <div className="relative flex space-x-3">
                      {/* Icon */}
                      <div>
                        <span className={`h-8 w-8 rounded-full ${display.bgColor} flex items-center justify-center ring-8 ring-white`}>
                          <span className="text-sm">{display.icon}</span>
                        </span>
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {activity.title}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {activity.description}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <time className="text-sm text-gray-500">
                              {formatCallDate(activity.activity_date)}
                            </time>
                            {activity.metadata && (
                              <button
                                onClick={() => toggleExpanded(activityIndex)}
                                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                title={isExpanded ? 'Hide details' : 'Show details'}
                              >
                                <svg 
                                  className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && activity.metadata && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                            {formatActivityDetails(activity)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="mt-6 text-center">
            <button
              onClick={loadMoreActivities}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Loading...
                </>
              ) : (
                'Load More Activities'
              )}
            </button>
          </div>
        )}

        {filteredActivities.length === 0 && searchTerm && (
          <div className="text-center py-8">
            <div className="text-gray-400 text-2xl mb-2">🔍</div>
            <p className="text-gray-600">No activities found matching "{searchTerm}"</p>
            <button
              onClick={() => setSearchTerm('')}
              className="mt-2 text-indigo-600 hover:text-indigo-800 text-sm"
            >
              Clear search
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityTimeline;